import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Loader2, Trophy, Target, BadgeCheck } from 'lucide-react';
import { cn, formatScore } from '@/lib/utils';
import { getMedalStyle } from '@/lib/medals';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { UserRankCard } from './UserRankCard';
import { useAuth } from '@/context/AuthContext';
import { RANKING_GLOBAL_LEADERBOARD } from '@/graphql/queries';
import type { RankingGlobalLeaderboardQuery } from '@/gql/graphql';

type LeaderboardItem = NonNullable<
    NonNullable<RankingGlobalLeaderboardQuery['globalLeaderboard']>['items']
>[number];

function Podium({ items }: { items: LeaderboardItem[] }) {
    if (items.length < 3) return null;

    const podium = [
        { item: items[1], rank: 2, size: 'sm' as const, mt: '', border: 'border-slate-300 ring-slate-200', scoreColor: 'text-slate-600' },
        { item: items[0], rank: 1, size: 'md' as const, mt: '-mt-4', border: 'border-amber-400 ring-amber-200 shadow-lg shadow-amber-200', scoreColor: 'text-amber-600' },
        { item: items[2], rank: 3, size: 'sm' as const, mt: '', border: 'border-amber-600 ring-amber-100', scoreColor: 'text-slate-600' },
    ];

    return (
        <div className="flex items-end justify-center gap-3 py-4">
            {podium.map(({ item, rank, size, mt, border, scoreColor }) => (
                <div key={item.user?.id} className={cn("flex flex-col items-center gap-1", mt)}>
                    {rank === 1 && <Trophy size={20} className="text-amber-400 mb-1" />}
                    <div className="relative">
                        <UserAvatar
                            src={item.user?.pictureUrl}
                            alt={item.user?.username ?? ''}
                            size={size}
                            className={cn("border-2 ring-2", border)}
                        />
                        <div className={cn(
                            "absolute -bottom-1 -right-1 rounded-full text-[10px] font-black flex items-center justify-center border-2 border-white",
                            rank === 1 ? "w-6 h-6 text-xs" : "w-5 h-5",
                            getMedalStyle(rank)
                        )}>
                            {rank}
                        </div>
                    </div>
                    <span className="text-xs font-bold text-slate-500 truncate max-w-[80px]">
                        {item.user?.username}
                    </span>
                    <span className={cn("font-black", rank === 1 ? "text-lg" : "text-sm", scoreColor)}>
                        {formatScore(item.totalScore ?? 0)}
                    </span>
                </div>
            ))}
        </div>
    );
}

function LeaderboardEntry({ item, rank, isCurrentUser }: {
    item: LeaderboardItem;
    rank: number;
    isCurrentUser: boolean;
}) {
    const { t } = useTranslation('bets');

    return (
        <div className={cn(
            "flex items-center gap-3 p-4 rounded-2xl border transition-all",
            isCurrentUser
                ? "bg-emerald-50/50 border-emerald-200 ring-1 ring-emerald-100"
                : "bg-white border-slate-100"
        )}>
            <div className="relative flex-shrink-0">
                <UserAvatar
                    src={item.user?.pictureUrl}
                    alt={item.user?.username ?? ''}
                    size="xs"
                />
                <div className={cn(
                    "absolute -bottom-1 -right-1 w-5 h-5 rounded-full text-[10px] font-black flex items-center justify-center border-2 border-white",
                    getMedalStyle(rank)
                )}>
                    {rank}
                </div>
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className={cn(
                        "font-bold truncate",
                        isCurrentUser ? "text-emerald-900" : "text-slate-900"
                    )}>
                        {item.user?.username ?? '?'}
                    </span>
                    {isCurrentUser && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 flex-shrink-0">
                            {t('leaderboard.you')}
                        </span>
                    )}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                    {t('ranking.global.racesPlayed', { count: item.raceCount ?? 0 })}
                </p>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
                <span className={cn(
                    "text-lg font-black",
                    (item.totalScore ?? 0) < 0
                        ? "text-red-500"
                        : isCurrentUser ? "text-emerald-600" : "text-slate-800"
                )}>
                    {formatScore(item.totalScore ?? 0)}
                </span>
                <span className="text-xs text-slate-400 font-medium">
                    {t('ranking.global.pts')}
                </span>
            </div>
        </div>
    );
}

export function GlobalRankingTab() {
    const { t } = useTranslation('bets');
    const { t: tc } = useTranslation();
    const { user } = useAuth();
    const [page, setPage] = useState(1);

    const { data, loading, error, fetchMore } = useQuery(RANKING_GLOBAL_LEADERBOARD, {
        variables: { pagination: { page: 1, limit: 20 } },
    });

    const leaderboard = data?.globalLeaderboard;
    const items = leaderboard?.items ?? [];
    const hasNextPage = leaderboard?.hasNextPage ?? false;

    // Compute raceCount for current user from their entry in the list, or fallback
    const currentUserEntry = items.find(item => item.user?.id === user?.id);
    const currentUserRaceCount = currentUserEntry?.raceCount ?? undefined;

    const handleLoadMore = async () => {
        const nextPage = page + 1;
        await fetchMore({
            variables: { pagination: { page: nextPage, limit: 20 } },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult?.globalLeaderboard) return prev;
                return {
                    globalLeaderboard: {
                        ...fetchMoreResult.globalLeaderboard,
                        items: [
                            ...(prev.globalLeaderboard?.items ?? []),
                            ...(fetchMoreResult.globalLeaderboard.items ?? []),
                        ],
                    },
                };
            },
        });
        setPage(nextPage);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20 text-slate-400">
                <Loader2 size={28} className="animate-spin text-emerald-600" />
            </div>
        );
    }

    if (error) {
        return <ErrorBanner message={tc('error.generic')} />;
    }

    if (items.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
                <Target size={40} className="text-slate-200 mb-3" />
                <p className="text-sm font-medium text-slate-400 mb-1">
                    {t('ranking.global.empty')}
                </p>
                <p className="text-xs text-slate-400">
                    {t('ranking.global.emptyDesc')}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <p className="text-xs text-slate-400 flex items-center gap-1">
                <BadgeCheck size={12} className="text-amber-500" />
                {t('ranking.global.officialOnly')}
            </p>

            <UserRankCard
                rank={leaderboard?.currentUserRank}
                totalScore={leaderboard?.currentUserScore}
                raceCount={currentUserRaceCount}
            />

            <Podium items={items} />

            <div className="space-y-2">
                {items.map((item, index) => (
                    <LeaderboardEntry
                        key={item.user?.id ?? index}
                        item={item}
                        rank={index + 1}
                        isCurrentUser={item.user?.id === user?.id}
                    />
                ))}
            </div>

            {hasNextPage && (
                <div className="flex justify-center pt-2 pb-4">
                    <button
                        onClick={handleLoadMore}
                        className="px-6 py-3 rounded-2xl font-bold text-sm text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors active:scale-95"
                    >
                        {t('ranking.global.loadMore')}
                    </button>
                </div>
            )}
        </div>
    );
}
